import { ViewType } from '@LSP/types';
import { usePersistentCallback } from '@prophecy/utils/react/hooks';
import { StableChildren } from '@prophecy/utils/react/StableChildren';
import { createContext } from '@prophecy/utils/react/useZustandContext';
import { parseJSON } from '@prophecy/utils/string';
import { useQueryClient } from '@tanstack/react-query';
import { isObject, noop } from 'lodash-es';
import { useEffect, useMemo, useState } from 'react';

import {
  useGraphQlMutation,
  useGraphQlMutationWithoutShareTokens,
  useGraphQlQueryWithoutShareTokens
} from '../../data/util';
import { isReplayMode, replayToken } from '../../utils/replayToken';
import { isShareMode, shareToken } from '../../utils/shareToken';
import { GetUserPreferenceQuery, SetUserPreferenceQuery } from './queries';
import { PreferenceContextType, SetPreference, UserPreferenceKeys } from './types';
import { usePreference, useSetPreferenceHistory } from './util';

type LegacyViewValue = ViewType;
type View = {
  simple: ViewType;
  advanced: ViewType;
};

export type UserPreferenceValues = Partial<{
  [UserPreferenceKeys.codeViewSplitEditor]: boolean;
  [UserPreferenceKeys.viewType]: View | LegacyViewValue;
  [UserPreferenceKeys.SQLVariantSamplingLimit]: number;
  [UserPreferenceKeys.OrchFabricOAuthReminder]: number;
  [UserPreferenceKeys.showAllCommitsInReplayBuilder]: boolean;
  [UserPreferenceKeys.replay]: { shareLink?: string; step?: number } | null;
  [UserPreferenceKeys.onboardingTourCompleted_v425]?: boolean;
  [UserPreferenceKeys.ideLayoutFlip]?: boolean;
  [UserPreferenceKeys.newUserSignupAutoProjectClone]?: boolean;
}>;

const { Context: UserPreferenceContext, Provider: UserPreferenceZustandProvider } = createContext<
  PreferenceContextType<UserPreferenceValues>
>({
  preferences: {},
  setPreference: noop
});

const PreferenceQueryKey = ['USER_PREFERENCE'];

const useGraphQlUserPreferenceQueryFn = () => {
  const isSharedProjectOrReplayMode = isShareMode() || isReplayMode();
  const isGuestUserLoggedIn = Boolean(shareToken.loggedInUserId || replayToken.loggedInUserID);
  const { data: _userPreferenceDataWithoutTokens, isInitialLoading: _isLoadingWithoutTokens } =
    useGraphQlQueryWithoutShareTokens(
      PreferenceQueryKey,
      GetUserPreferenceQuery,
      {
        refetchInterval: false,
        enabled: isSharedProjectOrReplayMode ? isGuestUserLoggedIn : true
      },
      {}
    );

  const shouldSkipUserPreference = isSharedProjectOrReplayMode && !isGuestUserLoggedIn;
  const userPreferenceData = shouldSkipUserPreference ? undefined : _userPreferenceDataWithoutTokens;
  const isLoading = shouldSkipUserPreference ? false : _isLoadingWithoutTokens;

  return {
    userPreferenceData,
    isLoading
  };
};

export function useUserPreferenceStore() {
  const [userPreferences, setUserPreferences] = useState<UserPreferenceValues>({});

  const { userPreferenceData, isLoading } = useGraphQlUserPreferenceQueryFn();

  const { mutateAsync: setUserPreferenceData } = useGraphQlMutation(SetUserPreferenceQuery);
  const { mutateAsync: setUserPreferenceDataWithShareOrReplayToken } =
    useGraphQlMutationWithoutShareTokens(SetUserPreferenceQuery);

  const queryClient = useQueryClient();
  useEffect(() => {
    if (!userPreferenceData) return;
    const parsedPreference: UserPreferenceValues = parseJSON(userPreferenceData.getUserPreference, {});
    setUserPreferences(parsedPreference);
  }, [userPreferenceData]);

  const persistPreference = async (preferences: UserPreferenceValues) => {
    const serializedPreferences = JSON.stringify(preferences);
    const isSharedProjectOrReplayMode = isShareMode() || isReplayMode();
    const isGuestUserLoggedIn = Boolean(shareToken.loggedInUserId || replayToken.loggedInUserID);
    if (isSharedProjectOrReplayMode) {
      if (isGuestUserLoggedIn) {
        await setUserPreferenceDataWithShareOrReplayToken({ userPreference: serializedPreferences });
      }
    } else {
      await setUserPreferenceData({ userPreference: serializedPreferences });
    }
    queryClient.invalidateQueries(PreferenceQueryKey);
  };

  const setPreference = useSetPreferenceHistory(
    userPreferences,
    setUserPreferences,
    persistPreference
  ) as SetPreference<UserPreferenceValues>;

  return { userPreferences, isLoading, setPreference };
}

export function UserPreferenceProvider({
  children,
  preferences,
  setPreference
}: {
  children: React.ReactNode;
  preferences: UserPreferenceValues;
  setPreference: SetPreference<UserPreferenceValues>;
}) {
  const memoizedContextValue = useMemo(() => {
    return { preferences, setPreference };
  }, [preferences, setPreference]);
  return (
    <UserPreferenceZustandProvider value={memoizedContextValue}>
      <StableChildren>{children}</StableChildren>
    </UserPreferenceZustandProvider>
  );
}

export function useUserPreference<T extends keyof UserPreferenceValues>(key: T) {
  return usePreference(UserPreferenceContext, key);
}

// Normalize the stored value to always be in object format
const normalizeViewType = (raw: UserPreferenceValues['viewType']): View => {
  if (isObject(raw)) {
    return {
      simple: raw.simple ?? ViewType.visual,
      advanced: raw.advanced ?? ViewType.code
    };
  }
  // Legacy case: raw is ViewType (code or visual)
  return {
    simple: ViewType.visual,
    advanced: raw ?? ViewType.code
  };
};
export function useViewPreference(isSimple: boolean): [ViewType, (viewType: ViewType) => void] {
  const [_viewType, setViewType] = useUserPreference(UserPreferenceKeys.viewType);

  const normalizedViewType = normalizeViewType(_viewType);

  // Migration: if legacy format detected, update preference to new format
  useEffect(() => {
    if (!isObject(_viewType)) {
      const current = normalizeViewType(_viewType);
      setViewType(current);
    }
  }, [_viewType, setViewType]);

  const _setViewType = usePersistentCallback((viewType: ViewType) => {
    setViewType((previous) => {
      const current = normalizeViewType(previous);
      return isSimple ? { ...current, simple: viewType } : { ...current, advanced: viewType };
    });
  });

  const viewType = useMemo(
    () => (isSimple ? normalizedViewType.simple : normalizedViewType.advanced),
    [isSimple, normalizedViewType]
  );

  return [viewType, _setViewType];
}

export function useOrchFabricOAuthReminder() {
  const [orchFabricOAuthReminder, setOrchFabricOAuthReminder] = useUserPreference(
    UserPreferenceKeys.OrchFabricOAuthReminder
  );

  return { orchFabricOAuthReminder, setOrchFabricOAuthReminder };
}
